import {useState, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {Grid, CircularProgress} from "@mui/material";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import ImgStaticCard from "@/shared/components/common/imgStaticCard/imgStaticCard";
import BoxKit from "@/shared/components/kit/box";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import CardBoxLayout from "@/modules/merchant/layout/cardBoxLayout";
import ContentInformation from "./informationReview";
import ValidationAfterPayment from "./payment/validationAfterPayment";
import {fetchUserLastInquiry, fetchWalletBalance} from "@/api/user/home";
import AwaitingApproval from "./informationReview/tabInformationReview/awaitApproval";
import {numberSeparator} from "@/hooks/useThousandsSeparator";
import AdminReject from "@/modules/user/components/credits/validation/payment/adminReject";
import ShahkarFailed from "@/modules/user/components/credits/validation/payment/shahkarFailed";


const STALE_TIME = 5 * 60 * 1000;
const LOADING_HEIGHT = "300px";

const STEP_GROUPS = {
    VALIDATION_FLOW: new Set([
        'set_provider_type', 'reject-loan'
    ]),
    AFTER_PAYMENT_FLOW: new Set([
        'set_pay',
        'set_guarantor_type', 'verify_other_guarantor', 'verify_myself_guarantor',
        'select_guarantor_failed', 'detect_financial_validator', 'detect_financial_validator_failed',
        'send_otp_financial', 'send_otp_financial_failed', 'get_financial_confirm_otp',
        'make_financial_report_failed', 'make_financial_report_success', 'make_financial_report_file','download_financial_report_file_success',
        'make_financial_report_file_failed','make_financial_report_file_pending', 'download_financial_report_file', 'download_financial_report_success',
        'download_financial_report_file_failed', 'resend_otp_financial', 'resend_otp_financial_success',
        'resend_otp_financial_failed', 'selector-judicial', 'selector-judicial-success', 'selector-judicial-failed',
        'get_judicial_confirm_otp', 'get_judicial_confirm_otp_success', 'get_judicial_confirm_otp_failed',
        'judicial_resend_otp', 'judicial_resend_otp_success', 'judicial_resend_otp_failed',
        'get_judicial_report', 'get_judicial_report_success', 'get_judicial_report_failed' ,'finish','make_financial_report_file_success','selector-judicial-reload'
    ]),
    AWAITING_APPROVAL_STATUSES: new Set([
        'await_admin', 'edit', 'accept', 'pending', 'edit-data-check',
        'accept-data-check', 'await_admin_check', 'await_accept_from_user'
    ])
} as const;

const isStepInGroup = (step: string | undefined, group: Set<string>): boolean => !!step && group.has(step);

const isInvalidStep = (step: string | undefined): boolean => !step || step === 'unknown' || step === '-';

const isOpenLoan = (step: string | undefined): boolean => step === 'open-loan';

const isAdminReject = (userInquiry: any): boolean => userInquiry?.status_manager_answer === "reject" && userInquiry?.step === "reject";

const isShahkarFailed = (userInquiry: any): boolean => userInquiry?.status_manager_answer === "shahkar_failed";

const isAwaitingDocument = (userInquiry: any): boolean => userInquiry?.status_manager_answer === "await_document";

const isAwaitingApproval = (userInquiry: any): boolean => STEP_GROUPS.AWAITING_APPROVAL_STATUSES.has(userInquiry?.status_manager_answer);

const shouldShowValidationFlow = (step: string | undefined): boolean => STEP_GROUPS.VALIDATION_FLOW.has(step ?? '');

const shouldShowAfterPaymentFlow = (step: string | undefined, payed: number | undefined): boolean => STEP_GROUPS.AFTER_PAYMENT_FLOW.has(step ?? '') && payed === 1;

const LoadingState = () => (
    <CardBoxLayout>
        <StackKit alignItems="center" justifyContent="center" height={LOADING_HEIGHT}>
            <CircularProgress/>
            <TypoGraphyKit variant="body2" sx={{mt: 2}}>
                در حال بارگذاری اطلاعات...
            </TypoGraphyKit>
        </StackKit>
    </CardBoxLayout>
);

const ErrorState = () => (
    <CardBoxLayout>
        <TypoGraphyKit variant="body1" color="error">
            خطا در دریافت اطلاعات. لطفاً مجدد تلاش کنید.
        </TypoGraphyKit>
    </CardBoxLayout>
);

const InfoMessage = ({title, message}: { title: string; message?: string }) => (
    <StackKit direction="row" sx={{width: "100%", justifyContent: "center", height: "486px", marginBottom: "30px", padding: "10px 30px", alignItems: "center"}}>
        <StackKit sx={{
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            background: "linear-gradient(97.77deg, rgba(0, 0, 0, 0.4) -9.73%, rgba(23, 23, 23, 0.281106) 16.04%, rgba(115, 103, 240, 0.25) 50.23%, rgba(65, 65, 65, 0.231942) 87.66%, rgba(0, 0, 0, 0.16) 109.42%)",
        }}>
            <TypoGraphyKit variant="h6" sx={{color: "#fff", textAlign: "center"}}>
                {title}
            </TypoGraphyKit>
            {message && (
                <TypoGraphyKit variant="body1" sx={{color: "#A4A4A4", textAlign: "center", mt: 2}}>
                    {message}
                </TypoGraphyKit>
            )}
        </StackKit>
    </StackKit>
);

const Validation = () => {
    const [selected, setSelected] = useState("all");

    const {data: userInquiry, isLoading: isLoadingInquiry, isError: isErrorInquiry} = useQuery({
        queryKey: ["UserLastInquiry-first-request"],
        queryFn: fetchUserLastInquiry,
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const {data: walletBalance, isLoading: isLoadingBalance, isError: isErrorBalance} = useQuery({
        queryKey: ["validation-wallet-balance"],
        queryFn: fetchWalletBalance,
    });

    const isLoading = isLoadingInquiry || isLoadingBalance;
    const payed = userInquiry?.payed;
    const step = userInquiry?.step;

    const dataOptionSelect = useMemo(() => [
        {
            label: `موجودی نقدی(ریال) : ${numberSeparator(`${walletBalance?.balance}`) ?? 0}`,
            value: "all",
        },
        {
            label: `موجودی اعتباری(ریال) : ${numberSeparator(`${walletBalance?.credit?.balance}`) ?? 0}`,
            value: "cash_20card",
        },
    ], [walletBalance]);

    if (isLoading) return <LoadingState />;
    if (isErrorInquiry || isErrorBalance) return <ErrorState />;

    if (isInvalidStep(step)) {
        return <InfoMessage title="وضعیت درخواست شما نامشخص است" message="لطفا با پشتیبانی بیست کارت تماس بگیرید" />;
    }

    if (isOpenLoan(step)) {
        return <InfoMessage
            title="شما همچنان اعتبار باقی مانده دارید. برای دریافت اعتبار جدید، ابتدا باید اعتبار فعلی خود را تسویه کنید."
            message={`مبلغ باقی مانده: ${userInquiry?.remainCredit?.toLocaleString()} ریال`}
        />;
    }

    if (isAdminReject(userInquiry)) {
        return <AdminReject message={userInquiry!.manage_answer} />;
    }

    if (isShahkarFailed(userInquiry)) {
        return <ShahkarFailed />;
    }

    if (isAwaitingDocument(userInquiry)) {
        return <ContentInformation userInquiry={userInquiry} walletBalance={walletBalance} />;
    }

    if (isAwaitingApproval(userInquiry)) {
        return <AwaitingApproval userInquiry={userInquiry} walletBalance={walletBalance} />;
    }

    if (shouldShowValidationFlow(step) || shouldShowAfterPaymentFlow(step, payed)) {
        const showSideCard = shouldShowValidationFlow(step);

        return (
            <StackKit direction="column" sx={{width: "100%"}}>
                <StackKit direction="row" sx={{justifyContent: {sm: 'start', xs: 'center'}, width: "100%", gap: "10px",paddingBottom: "20px"}}>
                    <TypoGraphyKit variant="h6">اعتبار بیست کارت</TypoGraphyKit>
                </StackKit>

                <Grid container sx={{
                    display: "flex",
                    flexDirection: {lg: "row", md: 'column-reverse', sm: 'column-reverse', xs: 'column-reverse'},
                    justifyContent: 'center',
                    alignItems: "center",
                    width: "100%"
                }}>
                    <Grid size={showSideCard ? {sm: 9, xs: 12} : 12} sx={{minHeight: "300px"}}>
                        {/*{shouldShowValidationFlow(step) && <ProviderStep />}*/}
                        {(shouldShowAfterPaymentFlow(step, payed) || shouldShowValidationFlow(step)) && <ValidationAfterPayment dataUserPAyment={userInquiry} />}
                    </Grid>

                    {showSideCard && (
                        <Grid className="flex flex-col items-center justify-center w-full h-[300px]" size={2}>
                            <ImgStaticCard imgSlider1="/card-out-simple-flat2.png" imgSlider2="/slide2.jpg" widthBox="200px"/>
                            <BoxKit sx={{minWidth: "200px", mt: 2}}>
                                <CustomSelectNormal
                                    label=""
                                    value={selected}
                                    onChange={(e) => setSelected(e.target.value)}
                                    options={dataOptionSelect}
                                    getOptionLabel={(item) => item.label}
                                    getOptionValue={(item) => item.value}
                                    width="100%"
                                />
                            </BoxKit>
                        </Grid>
                    )}
                </Grid>
            </StackKit>
        );
    }

    return (
        <CardBoxLayout>
            <TypoGraphyKit variant="body1" color="error">
                وضعیت نامعتبر. لطفاً با پشتیبانی تماس بگیرید.
            </TypoGraphyKit>
        </CardBoxLayout>
    );
};

export default Validation;